/**
 * Ask Rize transcript markdown renderer.
 *
 * Assistant turns stream markdown-shaped text (the ai-assistant system prompt
 * asks for bullets; the model also emits **bold**, pipe tables and `code`).
 * MessageBubble renders the result via dangerouslySetInnerHTML, so this module
 * extends the audited CC&R pipeline idiom (src/lib/ccrMarkdown.ts) to the
 * grammar the assistant actually produces: bold/italic, bullet + numbered
 * lists, pipe tables, inline + fenced code, headings (as bold paragraphs),
 * horizontal rules and paragraph line breaks.
 *
 * ORDER MATTERS — and is load-bearing for safety: HTML metacharacters (& < >)
 * are escaped FIRST so any literal markup in the model output (which can echo
 * db-derived strings) becomes inert text, and only THEN are the markdown
 * transforms applied. A transform can therefore only ever emit the tags this
 * module writes itself; it can never reconstruct an attacker-supplied tag.
 * Inline code spans are tokenized out before the * transforms run so an
 * asterisk inside `code` can never pair across a span boundary and chew the
 * emitted markup.
 *
 * Links are deliberately rendered as plain "text (url)" — never an <a>. The
 * assistant references in-app data; a model-echoed string must never become a
 * clickable navigation target (the audited CCR no-anchor posture).
 *
 * The output is still passed through sanitizeAskRizeHtml (src/lib/sanitize.ts)
 * before it reaches the DOM, so the rendered surface fails closed even if this
 * pipeline is later edited to emit something unexpected.
 *
 * Every emitted Tailwind class is a verbatim string literal — Tailwind v4
 * source-scans .ts files, so pieced-together class names would silently not
 * be generated (ccrMarkdown.ts's <hr> class is the working precedent).
 */

const CODE_PLACEHOLDER = '\uE000'

/** Inline transforms for one already-escaped text segment (never code/pre). */
function renderInline(text: string): string {
  // Tokenize code spans first: the * transforms must never see their contents
  // or match across their boundaries.
  const codes: string[] = []
  let out = text.replace(/`([^`\n]+)`/g, (_m, code: string) => {
    codes.push(`<code class="rounded bg-muted px-1 text-[0.85em]">${code}</code>`)
    return `${CODE_PLACEHOLDER}${codes.length - 1}${CODE_PLACEHOLDER}`
  })
  out = out
    .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, '$1 ($2)')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Single-asterisk italic requires non-space flanking (CommonMark-lite) so
    // prose multiplication ("length * width * height") is left literal.
    .replace(/\*([^\s*](?:[^*\n]*?[^\s*])?)\*/g, '<em>$1</em>')
  return out.replace(/\uE000(\d+)\uE000/g, (_m, i: string) => codes[Number(i)] ?? '')
}

const TABLE_SEPARATOR = /^\|(?:\s*:?-+:?\s*\|)+\s*$/
const UL_ITEM = /^[-*]\s+/
const OL_ITEM = /^\d+[.)]\s+/
const HEADING = /^#{1,6}\s+/
const HR = /^-{3,}$/

function tableCells(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => renderInline(c.trim()))
}

export function renderAskRizeMarkdown(md: string): string {
  const escaped = md
    .replace(/\uE000/g, '') // reserve the code-span placeholder char
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  const lines = escaped.split('\n')
  const blocks: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i] ?? ''
    const t = line.trim()

    if (t === '') {
      i += 1
      continue
    }

    // Fenced code block — collected first so nothing inside is transformed.
    // An unterminated fence (mid-stream) collects to EOF and still renders.
    if (t.startsWith('```')) {
      const content: string[] = []
      i += 1
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith('```')) {
        content.push(lines[i] ?? '')
        i += 1
      }
      i += 1 // skip the closing fence (or EOF)
      blocks.push(`<pre class="my-1 overflow-x-auto rounded bg-muted p-2 text-xs"><code>${content.join('\n')}</code></pre>`)
      continue
    }

    // Pipe table — only when the SECOND row is a |---| separator; a lone pipe
    // row is ordinary paragraph text.
    if (t.startsWith('|') && TABLE_SEPARATOR.test((lines[i + 1] ?? '').trim())) {
      const header = tableCells(t)
      i += 2 // header + separator
      const rows: string[][] = []
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(tableCells((lines[i] ?? '').trim()))
        i += 1
      }
      const head = header.map((h) => `<th class="border border-border px-2 py-1 text-left font-semibold">${h}</th>`).join('')
      // Normalize each body row to the header column count (GFM semantics) so a
      // ragged row can't add a phantom header-less column.
      const body = rows
        .map((r) => `<tr>${header.map((_h, ci) => `<td class="border border-border px-2 py-1">${r[ci] ?? ''}</td>`).join('')}</tr>`)
        .join('')
      blocks.push(`<div class="my-1 overflow-x-auto"><table class="w-full border-collapse text-xs"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`)
      continue
    }

    // Lists are flattened, not nested (indentation is trimmed) — deliberate:
    // a recursive nested-list parser isn't worth it for a ~370px chat bubble,
    // and the flat rendering stays readable.
    if (UL_ITEM.test(t)) {
      const items: string[] = []
      while (i < lines.length && UL_ITEM.test((lines[i] ?? '').trim())) {
        items.push(`<li>${renderInline((lines[i] ?? '').trim().replace(UL_ITEM, ''))}</li>`)
        i += 1
      }
      blocks.push(`<ul class="my-1 list-disc pl-4">${items.join('')}</ul>`)
      continue
    }

    if (OL_ITEM.test(t)) {
      // Respect the first item's number so a list that continues an enumeration
      // (starts at n!=1) renders n, n+1, … not 1, 2, … ('start' is allow-listed
      // in sanitizeAskRizeHtml).
      const startMatch = t.match(/^(\d+)/)
      const start = startMatch ? Number(startMatch[1]) : 1
      const items: string[] = []
      while (i < lines.length && OL_ITEM.test((lines[i] ?? '').trim())) {
        items.push(`<li>${renderInline((lines[i] ?? '').trim().replace(OL_ITEM, ''))}</li>`)
        i += 1
      }
      const startAttr = start !== 1 ? ` start="${start}"` : ''
      blocks.push(`<ol${startAttr} class="my-1 list-decimal pl-4">${items.join('')}</ol>`)
      continue
    }

    if (HEADING.test(t)) {
      blocks.push(`<p class="my-1 font-semibold">${renderInline(t.replace(HEADING, ''))}</p>`)
      i += 1
      continue
    }

    if (HR.test(t)) {
      blocks.push('<hr class="my-3 border-border">')
      i += 1
      continue
    }

    // Paragraph: consecutive plain lines joined with <br>. Stops at any block
    // boundary so a list/table/fence following prose still gets its shape.
    const para: string[] = [renderInline(t)]
    i += 1
    while (i < lines.length) {
      const nt = (lines[i] ?? '').trim()
      const isTableStart = nt.startsWith('|') && TABLE_SEPARATOR.test((lines[i + 1] ?? '').trim())
      if (
        nt === '' || nt.startsWith('```') || isTableStart ||
        UL_ITEM.test(nt) || OL_ITEM.test(nt) || HEADING.test(nt) || HR.test(nt)
      ) break
      para.push(renderInline(nt))
      i += 1
    }
    blocks.push(`<p class="my-1">${para.join('<br>')}</p>`)
  }

  return blocks.join('')
}
